/**
 * Phase 15.6O — Blocked Users management section.
 *
 * Shown inside Creator Hub. Lists currently blocked users with one-tap
 * unblock. Hidden when the viewer has zero blocks.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { optimizeImage } from "@/lib/mediaUrls";

const BlockedUsersSection = () => {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIds, setBusyIds] = useState(new Set());

  const reload = async () => {
    try {
      const r = await api.get("/blocks/me");
      setBlocked(r.data?.blocked || []);
    } catch (_e) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { reload(); }, []);

  const unblock = async (userId, name) => {
    setBusyIds((s) => new Set(s).add(userId));
    try {
      await api.delete(`/users/${userId}/block`);
      setBlocked((list) => list.filter((b) => b.user_id !== userId));
      toast.success(`Unblocked ${name}`);
    } catch (_e) {
      toast.error("Couldn't unblock — try again");
    } finally {
      setBusyIds((s) => {
        const next = new Set(s); next.delete(userId); return next;
      });
    }
  };

  if (loading) return null;
  if (blocked.length === 0) return null;

  return (
    <Card
      className="p-4 bg-gradient-to-br from-rose-900/15 to-purple-900/10 border-rose-500/30"
      data-testid="blocked-users-section"
    >
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-white font-bold text-base flex items-center gap-2">
          <span>🚫</span> Blocked Users
        </h3>
        <span className="text-[11px] text-rose-200/60 tabular-nums">{blocked.length}</span>
      </div>
      <ul className="space-y-1.5" data-testid="blocked-users-list">
        {blocked.map((b) => (
          <li
            key={b.user_id}
            className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.03] border border-white/5"
            data-testid={`blocked-user-row-${b.user_id}`}
          >
            <Link to={`/artist/${b.user_id}`} className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-full overflow-hidden bg-gradient-to-br from-purple-700 to-pink-700 flex items-center justify-center flex-shrink-0">
                {b.profile_photo ? (
                  <img src={optimizeImage(b.profile_photo, 'avatar')} alt={b.artist_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-sm font-bold">{(b.artist_name || "?").charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm truncate">{b.artist_name}</p>
                <p className="text-[10px] text-rose-200/50">Blocked {b.blocked_at ? new Date(b.blocked_at).toLocaleDateString() : ""}</p>
              </div>
            </Link>
            <Button
              type="button"
              size="sm"
              onClick={() => unblock(b.user_id, b.artist_name)}
              disabled={busyIds.has(b.user_id)}
              className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 text-xs h-8 px-2"
              data-testid={`blocked-user-unblock-${b.user_id}`}
            >
              {busyIds.has(b.user_id) ? "…" : "Unblock"}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
};

export default BlockedUsersSection;
